import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { MaintenancePlan } from "@/hooks/use-maintenance-plans";
import { FleetProvider } from "@/hooks/use-fleet-providers";
import { Switch } from "@/components/ui/switch";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { AlertCircle } from "lucide-react";

// Categorias de manutenção (valores em inglês para o banco)
const MAINTENANCE_CATEGORIES = {
  mechanical: 'Mecânica',
  tire: 'Pneus',
  suspension: 'Suspensão',
  electrical: 'Elétrica',
  bodywork: 'Funilaria e Pintura',
  other: 'Outros'
} as const;

const VALID_CATEGORIES = Object.keys(MAINTENANCE_CATEGORIES);

interface MaintenancePlanFormSheetProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: any) => void;
  plan?: MaintenancePlan | null;
  vehicles: Array<{ id: string; plate: string; make?: string; model?: string }>;
  isLoading?: boolean;
  onCreateWorkOrder?: (planId: string, workOrderData: any) => void;
  providers?: FleetProvider[];
  workOrders?: Array<{ id: string; plan_id: string | null; status: string }>;
}

export function MaintenancePlanFormSheet({
  isOpen,
  onClose,
  onSubmit,
  plan,
  vehicles,
  isLoading = false,
  onCreateWorkOrder,
  providers = [],
  workOrders = []
}: MaintenancePlanFormSheetProps) {
  const hasOpenWorkOrders = plan ? workOrders.some(
    wo => wo.plan_id === plan.id && (wo.status === 'open' || wo.status === 'in_progress')
  ) : false;

  const [formData, setFormData] = useState({
    name: "",
    type: "",
    maintenance_category: "",
    vehicle_id: "",
    periodicity_days: "",
    periodicity_km: "",
    next_execution_date: "",
    next_execution_km: "",
    status: "active"
  });

  const [isCreatingWorkOrder, setIsCreatingWorkOrder] = useState(false);
  const [workOrderData, setWorkOrderData] = useState({
    description: "",
    provider_id: "none",
    total_cost: "",
    opened_at: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    if (plan) {
      setFormData({
        name: plan.name,
        type: plan.type,
        maintenance_category: (plan as any).maintenance_category || "",
        vehicle_id: plan.vehicle_id,
        periodicity_days: plan.periodicity_days?.toString() || "",
        periodicity_km: plan.periodicity_km?.toString() || "",
        next_execution_date: plan.next_execution_date ? plan.next_execution_date.split('T')[0] : "",
        next_execution_km: plan.next_execution_km?.toString() || "",
        status: plan.status
      });
    } else {
      setFormData({
        name: "",
        type: "",
        maintenance_category: "",
        vehicle_id: "",
        periodicity_days: "",
        periodicity_km: "",
        next_execution_date: "",
        next_execution_km: "",
        status: "active"
      });
    }
    setIsCreatingWorkOrder(false);
    setWorkOrderData({
      description: "",
      provider_id: "none",
      total_cost: "",
      opened_at: new Date().toISOString().split('T')[0]
    });
  }, [plan, isOpen]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validação de campos obrigatórios
    const sanitizedName = formData.name.trim();
    if (!sanitizedName) {
      console.error('Nome do plano é obrigatório');
      return;
    }
    
    if (!formData.type) {
      console.error('Tipo é obrigatório');
      return;
    }
    
    // Validar vehicle_id ao CRIAR (não ao editar)
    if (!plan && (!formData.vehicle_id || formData.vehicle_id.trim() === '')) {
      console.error('Selecione um veículo para o plano de manutenção');
      return;
    }
    
    // Validação de categoria
    if (formData.maintenance_category && !VALID_CATEGORIES.includes(formData.maintenance_category)) {
      console.error('Categoria inválida:', formData.maintenance_category);
      return;
    }
    
    // Helper para normalizar numéricos (aceita vírgula)
    const parseNumeric = (value: string): number | null => {
      if (!value || value.trim() === '') return null;
      const cleaned = value.replace(',', '.').trim();
      const parsed = parseFloat(cleaned);
      return isFinite(parsed) ? parsed : null;
    };
    
    const parseInteger = (value: string): number | null => {
      if (!value || value.trim() === '') return null;
      const cleaned = value.replace(',', '.').trim();
      const parsed = parseInt(cleaned);
      return isFinite(parsed) ? parsed : null;
    };
    
    const submitData: any = {
      name: sanitizedName,
      type: formData.type,
      maintenance_category: formData.maintenance_category || null,
      periodicity_days: parseInteger(formData.periodicity_days),
      periodicity_km: parseNumeric(formData.periodicity_km),
      next_execution_km: parseNumeric(formData.next_execution_km),
      next_execution_date: formData.next_execution_date || null,
      status: formData.status
    };

    // Só incluir vehicle_id se não estiver vazio
    if (formData.vehicle_id && formData.vehicle_id.trim() !== '') {
      submitData.vehicle_id = formData.vehicle_id;
    }

    // Incluir o id quando está editando
    if (plan?.id) {
      submitData.id = plan.id;
    }

    onSubmit(submitData);

    if (!plan && isCreatingWorkOrder && onCreateWorkOrder) {
      const woData = {
        ...workOrderData,
        total_cost: workOrderData.total_cost ? parseFloat(workOrderData.total_cost) : 0,
        provider_id: workOrderData.provider_id === "none" ? null : workOrderData.provider_id
      };
      onCreateWorkOrder('temp_plan_id', woData);
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent side="right" className="w-full sm:w-[550px] overflow-hidden flex flex-col">
        <SheetHeader>
          <SheetTitle>{plan ? 'Editar Plano de Manutenção' : 'Novo Plano de Manutenção'}</SheetTitle>
          <SheetDescription>
            {plan 
              ? 'Atualize as informações do plano de manutenção'
              : 'Configure um novo plano de manutenção preventiva ou corretiva'
            }
          </SheetDescription>
        </SheetHeader>

        <ScrollArea className="flex-1">
          <form id="maintenance-plan-form" onSubmit={handleSubmit} className="space-y-6 py-4">
            {/* Identificação */}
            <div className="space-y-4">
              <h3 className="font-medium text-sm">Identificação</h3>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Nome do Plano *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Ex: Troca de óleo mensal"
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="type">Tipo *</Label>
                    <Select
                      value={formData.type}
                      onValueChange={(value) => setFormData({ ...formData, type: value })}
                    >
                      <SelectTrigger id="type">
                        <SelectValue placeholder="Selecione" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="preventive">Preventiva</SelectItem>
                        <SelectItem value="corrective">Corretiva</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="maintenance_category">Categoria</Label>
                    <Select
                      value={formData.maintenance_category}
                      onValueChange={(value) => setFormData({ ...formData, maintenance_category: value })}
                    >
                      <SelectTrigger id="maintenance_category">
                        <SelectValue placeholder="Selecione" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="mechanical">Mecânica</SelectItem>
                        <SelectItem value="tire">Pneus</SelectItem>
                        <SelectItem value="suspension">Suspensão</SelectItem>
                        <SelectItem value="electrical">Elétrica</SelectItem>
                        <SelectItem value="bodywork">Funilaria e Pintura</SelectItem>
                        <SelectItem value="other">Outros</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="vehicle_id">Veículo *</Label>
                  <Select
                    value={formData.vehicle_id}
                    onValueChange={(value) => setFormData({ ...formData, vehicle_id: value })}
                  >
                    <SelectTrigger id="vehicle_id">
                      <SelectValue placeholder="Selecione o veículo" />
                    </SelectTrigger>
                    <SelectContent>
                      {vehicles.map((vehicle) => (
                        <SelectItem key={vehicle.id} value={vehicle.id}>
                          {vehicle.plate} - {vehicle.make} {vehicle.model}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Periodicidade */}
            <div className="space-y-4 pt-4 border-t">
              <h3 className="font-medium text-sm">Periodicidade</h3>
              <p className="text-xs text-muted-foreground">Preencha pelo menos um dos campos abaixo</p>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="periodicity_days">A cada (dias)</Label>
                  <Input
                    id="periodicity_days"
                    type="number"
                    min="0"
                    value={formData.periodicity_days}
                    onChange={(e) => setFormData({ ...formData, periodicity_days: e.target.value })}
                    placeholder="Ex: 30"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="periodicity_km">A cada (km)</Label>
                  <Input
                    id="periodicity_km"
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.periodicity_km}
                    onChange={(e) => {
                      const value = e.target.value.replace(',', '.');
                      setFormData({ ...formData, periodicity_km: value });
                    }}
                    placeholder="Ex: 5000"
                  />
                </div>
              </div>
            </div>

            {/* Próxima Execução */}
            <div className="space-y-4 pt-4 border-t">
              <h3 className="font-medium text-sm">Próxima Execução</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="next_execution_date">Data</Label>
                  <Input
                    id="next_execution_date"
                    type="date"
                    value={formData.next_execution_date}
                    onChange={(e) => setFormData({ ...formData, next_execution_date: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="next_execution_km">Quilometragem</Label>
                  <Input
                    id="next_execution_km"
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.next_execution_km}
                    onChange={(e) => {
                      const value = e.target.value.replace(',', '.');
                      setFormData({ ...formData, next_execution_km: value });
                    }}
                    placeholder="Ex: 15000"
                  />
                </div>
              </div>
            </div>

            {/* Status */}
            <div className="space-y-4 pt-4 border-t">
              <h3 className="font-medium text-sm">Status</h3>
              <div className="space-y-2">
                <Select
                  value={formData.status}
                  onValueChange={(value) => setFormData({ ...formData, status: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">
                      Pendente {hasOpenWorkOrders && "(OSs abertas)"}
                    </SelectItem>
                    <SelectItem value="active">Ativo</SelectItem>
                    <SelectItem 
                      value="inactive" 
                      disabled={hasOpenWorkOrders}
                    >
                      Inativo {hasOpenWorkOrders && "(OSs abertas)"}
                    </SelectItem>
                    <SelectItem 
                      value="completed" 
                      disabled={hasOpenWorkOrders}
                    >
                      Concluído {hasOpenWorkOrders && "(OSs abertas)"}
                    </SelectItem>
                  </SelectContent>
                </Select>
                {hasOpenWorkOrders && (
                  <div className="flex items-start gap-2 p-3 bg-amber-500/10 rounded-md border border-amber-500/20">
                    <AlertCircle className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-amber-600 dark:text-amber-400">
                      Não é possível marcar como pendente, inativo ou concluído enquanto houver ordens de serviço abertas ou em andamento
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Criar OS Imediatamente */}
            {!plan && (
              <div className="space-y-4 pt-4 border-t">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="create_wo_toggle">Criar Ordem de Serviço Imediatamente</Label>
                    <p className="text-xs text-muted-foreground">
                      Cria uma OS automaticamente ao salvar o plano
                    </p>
                  </div>
                  <Switch
                    id="create_wo_toggle"
                    checked={isCreatingWorkOrder}
                    onCheckedChange={setIsCreatingWorkOrder}
                  />
                </div>

                {isCreatingWorkOrder && (
                  <div className="space-y-4 pl-4 border-l-2 border-primary/20">
                    <div className="space-y-2">
                      <Label htmlFor="wo_description">Descrição da OS</Label>
                      <Textarea
                        id="wo_description"
                        value={workOrderData.description}
                        onChange={(e) => setWorkOrderData({ ...workOrderData, description: e.target.value })}
                        placeholder="Descreva o serviço a ser realizado..."
                        rows={3}
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="wo_provider">Fornecedor</Label>
                        <Select
                          value={workOrderData.provider_id}
                          onValueChange={(value) => setWorkOrderData({ ...workOrderData, provider_id: value })}
                        >
                          <SelectTrigger id="wo_provider">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Nenhum</SelectItem>
                            {providers.map((provider) => (
                              <SelectItem key={provider.id} value={provider.id}>
                                {provider.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="wo_cost">Custo Estimado</Label>
                        <Input
                          id="wo_cost"
                          type="number"
                          min="0"
                          step="0.01"
                          value={workOrderData.total_cost}
                          onChange={(e) => setWorkOrderData({ ...workOrderData, total_cost: e.target.value })}
                          placeholder="0.00"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="wo_opened_at">Data de Abertura</Label>
                      <Input
                        id="wo_opened_at"
                        type="date"
                        value={workOrderData.opened_at}
                        onChange={(e) => setWorkOrderData({ ...workOrderData, opened_at: e.target.value })}
                      />
                    </div>
                  </div>
                )}
              </div>
            )}
          </form>
        </ScrollArea>

        <SheetFooter className="border-t pt-4 mt-auto px-6">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={isLoading}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            form="maintenance-plan-form"
            disabled={isLoading}
          >
            {isLoading ? "Salvando..." : plan ? "Salvar Alterações" : "Criar Plano"}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
